import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, ShoppingBag, ArrowRight, Package, XCircle } from "lucide-react";
import PaymentStatusShell from "./PaymentStatusShell";
import { getPaymentQueryData } from "./paymentStatusQuery";
import { verifyEpsPayment, verifyStripePayment } from "../../services/order.api";

// EPS only reports a payment once it is finished, and the API spaces
// its own lookups, so the page asks every few seconds for a couple of minutes
// before handing the customer to their orders page (the server keeps checking).
const EPS_POLL_MS = 5000;
const EPS_POLL_FOR_MS = 2 * 60 * 1000;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { paySystemName, status, transactionID, needsVerification } =
    getPaymentQueryData(searchParams);

  // Stripe sends the customer back without a status, so the session is
  // confirmed against the API rather than trusting the redirect.
  const [verified, setVerified] = useState(needsVerification ? null : true);

  useEffect(() => {
    if (!needsVerification) return;
    let cancelled = false;
    verifyStripePayment(transactionID)
      .then((res) => {
        if (!cancelled) setVerified(res?.data?.payment?.status === "paid");
      })
      .catch(() => {
        // The webhook settles the payment regardless; don't claim failure.
        if (!cancelled) setVerified(true);
      });
    return () => {
      cancelled = true;
    };
  }, [needsVerification, transactionID]);

  const isCancelled = status === "cancel";
  const isCOD = paySystemName === "cashOnDelivery";

  // EPS lands here with the payment's server-side status at redirect time.
  // Reaching the success URL only means the customer finished the hosted page,
  // so anything but "paid" is re-asked of the API, which confirms it with EPS
  // itself — the page never decides on its own that a payment went through.
  const isEpsReturn = !!status && !isCancelled && !!transactionID;
  const [epsStatus, setEpsStatus] = useState(status);
  const [epsPolling, setEpsPolling] = useState(isEpsReturn && status !== "paid");

  useEffect(() => {
    if (!isEpsReturn || status === "paid") return;
    let stopped = false;
    let timer;
    const startedAt = Date.now();

    const check = async () => {
      try {
        const res = await verifyEpsPayment(transactionID);
        const next = res?.data?.payment?.status;
        if (stopped) return;
        if (next) setEpsStatus(next);
        if (next === "paid" || next === "refunded") {
          setEpsPolling(false);
          return;
        }
      } catch {
        // Rate-limited or offline — keep the last known status and try again.
      }
      if (stopped) return;
      if (Date.now() - startedAt >= EPS_POLL_FOR_MS) {
        setEpsPolling(false);
        return;
      }
      timer = setTimeout(check, EPS_POLL_MS);
    };

    check();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [isEpsReturn, status, transactionID]);

  if (isEpsReturn && epsStatus !== "paid") {
    const view =
      epsStatus === "refunded"
        ? {
            // EPS took this payment, but the order was already paid through an
            // earlier attempt (or cancelled), so it is queued to be returned.
            icon: <XCircle size={40} />,
            tone: "warning",
            title: "This order was already paid",
            subtitle:
              "We received this payment, but your order had already been paid. This extra payment will be refunded to you — contact support with the transaction ID below if you have questions.",
          }
        : epsPolling
        ? {
            icon: <Package size={40} />,
            tone: "info",
            title: "Confirming your payment…",
            subtitle: "Hang on a moment while we check with the payment provider.",
          }
        : epsStatus === "failed"
        ? {
            icon: <XCircle size={40} />,
            tone: "danger",
            title: "Payment could not be verified",
            subtitle:
              "The payment provider has no completed payment for this order yet. If money left your account it will still be matched automatically — check your orders shortly.",
          }
        : {
            icon: <Package size={40} />,
            tone: "info",
            title: "Payment is still being confirmed",
            subtitle:
              "The payment provider hasn't confirmed it yet. We keep checking — your order will update on its own.",
          };
    return (
      <PaymentStatusShell
        icon={view.icon}
        tone={view.tone}
        badge="Payment Status"
        title={view.title}
        subtitle={view.subtitle}
        transactionID={transactionID}
        actions={
          <a href="/my-orders" className="ps-btn ps-btn-primary">
            <ArrowRight size={15} /> Go to My Orders
          </a>
        }
      />
    );
  }

  if (verified === null) {
    return (
      <PaymentStatusShell
        icon={<Package size={40} />}
        tone="info"
        badge="Confirming"
        title="Confirming your payment…"
        subtitle="Hang on a moment while we check with the payment provider."
        transactionID={transactionID}
      />
    );
  }

  if (verified === false) {
    return (
      <PaymentStatusShell
        icon={<XCircle size={40} />}
        tone="warning"
        badge="Payment Status"
        title="We couldn't confirm this payment"
        subtitle="If money left your account it will show up on the order shortly. Check your orders, and try again from there if it still says unpaid."
        transactionID={transactionID}
        actions={
          <a href="/my-orders" className="ps-btn ps-btn-primary">
            <ArrowRight size={15} /> Go to My Orders
          </a>
        }
      />
    );
  }

  const cfg = isCancelled
    ? {
        icon: <Package size={40} />,
        tone: "warning",
        badge: "Order Status",
        title: "Order Not Placed",
        subtitle: "Your order was cancelled before completion. Head back whenever you're ready to try again.",
        note: "Items in your cart are still saved.",
      }
    : isCOD
    ? {
        icon: <ShoppingBag size={40} />,
        tone: "info",
        badge: "Order Confirmed",
        title: "Order Placed!",
        subtitle: "Your cash-on-delivery order is confirmed. The kitchen will review it shortly — you can track progress from your orders page.",
        note: "You'll pay when your order arrives.",
      }
    : {
        icon: <CheckCircle size={40} />,
        tone: "success",
        badge: "Payment Confirmed",
        title: "Payment Successful!",
        subtitle: "Your payment went through and your order is now in the queue. Track it live from your orders page.",
        note: "A confirmation has been recorded with your transaction ID above.",
      };

  return (
    <PaymentStatusShell
      icon={cfg.icon}
      tone={cfg.tone}
      badge={cfg.badge}
      title={cfg.title}
      subtitle={cfg.subtitle}
      transactionID={transactionID}
      note={cfg.note}
      actions={
        <>
          <a href="/my-orders" className="ps-btn ps-btn-primary">
            <ArrowRight size={15} /> Track Order
          </a>
          <a href="/" className="ps-btn ps-btn-outline">
            Continue Shopping
          </a>
        </>
      }
    />
  );
}
